// ─────────────────────────────────────────────
//  ConversationView — the detail pane (Phase 5 messaging / UI-SPEC
//  "Conversation View"). Header (name + sub-line) + an aria-live message list
//  of MessageBubbles + the MessageComposer.
//
//  • Header: name 18/700; sub-line "N members" for a channel, "role · SUBROLE"
//    for a DM (AIC suffix " · AIC"). A mobile "← Messages" back affordance
//    returns to the list.
//  • Message list: aria-live="polite" so a screen reader announces new
//    incoming messages; auto-scrolls to newest via bottomRef.scrollIntoView.
//    showSender is true for GROUP channels (not DMs). mine = senderId === uid.
//  • Composer wired to send; readOnlyReason passed when the user can't post to
//    this channel (canPostToChannel false — D-10 UI hint, T-05-15).
//  • Incoming messages are marked read when shown.
//
//  The selection prop is { type:"channel", id } or
//  { type:"dm", id, contactId, name }.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useRef } from "react";
import { useMessaging } from "../context/MessagingContext";
import { useAuth } from "../context/AuthContext";
import { canPostToChannel } from "../domain/channels";
import MessageBubble from "./MessageBubble";
import MessageComposer from "./MessageComposer";
import { useToast } from "../context/ToastContext";
import tokens from "../theme/tokens";

const { color, font, space } = tokens;

const READ_ONLY_COPY = "You can read this channel but can't post here.";

// Per-channel empty-state copy (UI-SPEC). DM threads get the friendlier hello.
const EMPTY = {
  channel: {
    head: "No messages yet.",
    body: "Start the conversation — your message reaches everyone in this channel.",
  },
  dm: { head: "No messages yet.", body: "Say hello 👋" },
};

function MessageList({ messages, uid, showSender }) {
  const bottomRef = useRef(null);
  useEffect(() => {
    // scrollIntoView is unimplemented in jsdom and may be absent in non-DOM
    // environments — guard so the effect never throws.
    if (typeof bottomRef.current?.scrollIntoView === "function") {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  return (
    <div style={styles.body} aria-live="polite" aria-label="Messages">
      {messages.map((m) => (
        <MessageBubble
          key={m.id}
          message={m}
          mine={m.senderId === uid}
          showSender={showSender}
        />
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

export default function ConversationView({ selection, onBack }) {
  const { channels, messagesByChannel, dmMessages, send, sendDM, markRead, markDMRead } =
    useMessaging();
  const { user, isAIC, isAdmin } = useAuth();
  const toast = useToast();
  const uid = user?.uid || null;

  const isDm = selection?.type === "dm";
  const channel = !isDm ? channels.find((c) => c.id === selection?.id) : null;

  // Messages for this conversation.
  const messages = useMemo(() => {
    if (isDm) {
      const otherId = selection?.contactId;
      return (dmMessages || []).filter(
        (m) =>
          (m.senderId === otherId && m.recipientId === uid) ||
          (m.senderId === uid && m.recipientId === otherId)
      );
    }
    return messagesByChannel?.[selection?.id] || [];
  }, [isDm, selection, dmMessages, messagesByChannel, uid]);

  // Mark incoming messages read when the conversation is shown / updates. A
  // session-local set of already-marked ids prevents re-issuing the same write
  // on every snapshot burst before the readBy/isRead round-trips.
  const markedRef = useRef(new Set());
  useEffect(() => {
    for (const m of messages) {
      if (markedRef.current.has(m.id)) continue;
      if (isDm) {
        if (m.recipientId === uid && !m.isRead) { markedRef.current.add(m.id); markDMRead(m.id); }
      } else if (m.senderId !== uid && !(m.readBy || []).includes(uid)) {
        markedRef.current.add(m.id);
        markRead(m.id);
      }
    }
  }, [messages, isDm, uid, markRead, markDMRead]);

  if (!selection) {
    return (
      <div style={styles.pane}>
        <div style={styles.placeholder}>Select a conversation to start messaging.</div>
      </div>
    );
  }

  // Header name + sub-line.
  const name = isDm ? selection.name : channel?.label || "Conversation";
  const memberN = channel
    ? (channel.count != null ? channel.count : (channel.memberIds || []).length)
    : 0;
  const subLine = isDm
    ? "Direct message"
    : `${memberN} member${memberN === 1 ? "" : "s"}`;

  // Group channels show the sender name above incoming bubbles; DMs don't.
  const showSender = !isDm;

  // Read-only hint (D-10): server rule is the real boundary.
  const canPost = isDm
    ? true
    : canPostToChannel(channel, { role: user?.role, isAIC, isAdmin });
  const readOnlyReason = canPost ? undefined : READ_ONLY_COPY;

  const handleSend = async (body) => {
    try {
      if (isDm) {
        await sendDM({ recipientId: selection.contactId, recipientName: selection.name, body });
      } else {
        await send(channel, body);
      }
    } catch (err) {
      console.error("Send failed:", err);
      toast.error("Message not sent. Check your connection and try again.");
      throw err;
    }
  };

  const empty = isDm ? EMPTY.dm : EMPTY.channel;

  return (
    <div style={styles.pane}>
      <div style={styles.header}>
        {onBack && (
          <button
            type="button"
            style={styles.back}
            onClick={onBack}
            aria-label="Back to channels"
          >
            ← Messages
          </button>
        )}
        <div style={styles.headerText}>
          <div style={styles.headerName}>{name}</div>
          <div style={styles.headerSub}>{subLine}</div>
        </div>
      </div>

      {messages.length === 0 ? (
        <div style={styles.body} aria-live="polite" aria-label="Messages">
          <div style={styles.emptyState}>
            <div style={styles.emptyHead}>{empty.head}</div>
            <div style={styles.emptyBody}>{empty.body}</div>
          </div>
        </div>
      ) : (
        <MessageList messages={messages} uid={uid} showSender={showSender} />
      )}

      <MessageComposer onSend={handleSend} readOnlyReason={readOnlyReason} />
    </div>
  );
}

const styles = {
  pane: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    background: color.surface.base,
    fontFamily: font.family,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    padding: `${space.md}px ${space.lg}px`,
    borderBottom: `1px solid ${color.border.default}`,
    background: color.surface.card,
    flexShrink: 0,
  },
  back: {
    minHeight: 44,
    minWidth: 44,
    background: "transparent",
    border: "none",
    color: color.text.secondary,
    cursor: "pointer",
    fontSize: font.size.body,
    fontFamily: "inherit",
  },
  headerText: { minWidth: 0 },
  headerName: {
    fontSize: font.size.heading, // 18
    fontWeight: font.weight.bold, // 700
    color: color.text.primary,
    lineHeight: font.lineHeight.tight,
  },
  headerSub: {
    fontSize: font.size.label, // 12
    fontWeight: font.weight.bold, // 700
    color: color.text.secondary,
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: space.md,
    background: color.surface.base, // #e9eef5 dominant ground
  },
  emptyState: {
    textAlign: "center",
    padding: space.xl,
  },
  emptyHead: {
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    marginBottom: space.xs,
  },
  emptyBody: {
    fontSize: font.size.label,
    color: color.text.muted,
    lineHeight: font.lineHeight.normal,
  },
  placeholder: {
    margin: "auto",
    color: color.text.muted,
    fontSize: font.size.body,
  },
};
